<template>
    <div
        v-if="visibleChildren.length"
        ref="tileRef"
    >
        <div class="tile-row">
            <div
                :key="visibleChildren[0]?.id"
                class="tile-label-wrapper"
            >
                <CustomPageBlock
                    :model-value="visibleChildren[0]?.currentValue"
                    :block="visibleChildren[0]"
                    :data-testid="`custom-page-block-${kebabCase((visibleChildren[0] as ExtendedBlock<CPInputConfiguration>)?.fieldName || visibleChildren[0]?.type)}`"
                />
            </div>

            <div
                v-if="$slots['extra-buttons'] || isToggleVisible"
                class="tile-row-btns"
            >
                <slot name="extra-buttons" />
                <div
                    v-if="isToggleVisible"
                    :class="{
                        isToggled: toggled && !NON_AUTO_REVEALING_BEHAVIORS.includes(effectiveRevealBehavior),
                        error: NON_AUTO_REVEALING_BEHAVIORS.includes(effectiveRevealBehavior) && childrenError,
                    }"
                    class="container-minor-btn"
                    data-testid="container-item-toggle"
                    :data-toggle="revealId"
                    @click="toggle"
                >
                    <i
                        :class="revealIcon || TOGGLE_ICONS[effectiveRevealBehavior] || 'mi-chevron-down'"
                        :title="t('expand')"
                    />
                </div>
            </div>
        </div>

        <template v-if="effectiveRevealBehavior === 'MODAL'">
            <ErrorText
                v-if="childrenError"
                :error="childrenError"
            />

            <Dialog
                v-model="toggled"
                :append-to-body="t('apply')"
                apply-button-class="container-minor-btn"
            >
                <div
                    class="tile-content opened"
                    :data-testid="testIdPrefix + '-components'"
                >
                    <div
                        v-for="block in visibleChildren"
                        :key="block.id"
                        :class="itemClass"
                    >
                        <CustomPageBlock
                            :model-value="block.currentValue"
                            :block="block"
                            :data-testid="`custom-page-block-${kebabCase((block as ExtendedBlock<CPInputConfiguration>).fieldName || block.type)}`"
                        />
                    </div>
                </div>
            </Dialog>
        </template>
        <template v-else-if="effectiveRevealBehavior === 'POPOVER'">
            <ErrorText
                v-if="childrenError"
                :error="childrenError"
            />
            <Popover
                v-model="toggled"
                :reference="reference"
                :max-width="sm ? undefined : 400"
                keep-alive
                close-on-reference-click
            >
                <div
                    class="tile-content tile-popover-content opened"
                    :data-testid="testIdPrefix + '-components'"
                >
                    <template v-for="(block, idx) in visibleChildren">
                        <div
                            v-if="idx > 0"
                            :key="block.id"
                            :class="itemClass"
                        >
                            <CustomPageBlock
                                :model-value="block.currentValue"
                                :block="block"
                                :data-testid="`custom-page-block-${kebabCase((block as ExtendedBlock<CPInputConfiguration>).fieldName || block.type)}`"
                            />
                        </div>
                    </template>
                </div>
            </Popover>
        </template>
        <template v-else>
            <transition
                v-if="isToggleVisible || effectiveRevealBehavior === 'STATIC'"
                name="container-item-toggle"
                @enter="onEnter"
                @after-enter="onLeave"
                @before-leave="onEnter"
                @after-leave="onLeave"
            >
                <div
                    v-show="toggled"
                    class="tile-content"
                    :class="{ opened: toggled }"
                    :data-testid="testIdPrefix + '-components'"
                >
                    <div
                        v-for="(block, index) in visibleChildren"
                        :key="block.id"
                        :class="itemClass"
                    >
                        <template v-if="index > 0">
                            <CustomPageBlock
                                :model-value="block.currentValue"
                                :block="block"
                                :data-testid="`custom-page-block-${kebabCase((block as ExtendedBlock<CPInputConfiguration>).fieldName || block.type)}`"
                            />
                        </template>
                    </div>
                </div>
            </transition>
        </template>
    </div>
</template>

<script setup lang="ts">
import { computed, inject, ref, useId, watch } from 'vue'
import { CustomPageBlock } from 'widget-components/integrations/CustomPage'
import { kebabCase } from 'change-case'
import type {
    CPInputConfiguration,
    ExtendedBlock,
    RevealBehavior,
} from 'widget-components/integrations/CustomPage/types'
import Dialog from 'widget-components/atoms/Dialog/Dialog.vue'
import Popover from 'widget-components/atoms/Popover/Popover.vue'
import { useI18n } from 'widget-components/utilities/i18n'
import ErrorText from 'widget-components/components/ErrorText/ErrorText.vue'
import type { GetConditionValueInject } from 'widget-components/composables/conditions/useConditions'
import { flattenBlocks } from 'widget-components/utilities/containers/flatten-blocks'
import { useBreakpoints } from 'widget-components/composables/breakpoints'
import { isNotNil } from 'ramda'

const props = withDefaults(defineProps<{
    children: ExtendedBlock[]
    revealBehavior: RevealBehavior
    revealIcon?: string
    initiallyToggled?: boolean
    itemClass?: string
    testIdPrefix?: string
}>(), {
    initiallyToggled: false,
    testIdPrefix: 'container',
})

const { t } = useI18n()

const revealId = useId()
const tileRef = ref<HTMLElement>()
const reference = computed(() => tileRef.value?.querySelector<HTMLElement>(`[data-toggle="${revealId}"]`) || tileRef.value)

const INITIALLY_EXPANDED_BEHAVIORS: RevealBehavior[] = ['STATIC', 'INLINE_EXPANDED']
const NON_AUTO_REVEALING_BEHAVIORS: RevealBehavior[] = ['MODAL', 'POPOVER']

const TOGGLE_ICONS: Partial<Record<RevealBehavior, string>> = {
    MODAL: 'mi-expand',
    POPOVER: 'mi-menu',
}

const { sm, initialized } = useBreakpoints()

const getDisplayValue = inject<GetConditionValueInject>('getDisplayValue', (_: string) => true)
const cpIsLoading = inject('isLoading', ref(false))

const effectiveRevealBehavior = computed(() => {
    if (props.revealBehavior === 'INLINE_EXPANDED_SM') {
        return sm.value ? 'INLINE_EXPANDED' : 'STATIC'
    }
    if (props.revealBehavior === 'INLINE_COLLAPSED_SM') {
        return sm.value ? 'INLINE_COLLAPSED' : 'STATIC'
    }
    return props.revealBehavior
})

const toggled = ref(props.initiallyToggled)

const visibleChildren = computed(() =>
    props.children.filter((b: ExtendedBlock) => getDisplayValue(b.id)),
)

const isToggleVisible = computed(() =>
    effectiveRevealBehavior.value !== 'STATIC'
    && visibleChildren.value
        .map((b: ExtendedBlock) => getDisplayValue(b.id))
        .filter(isNotNil)
        .length > 1,
)

const childrenError = computed(() =>
    flattenBlocks(props.children).find(b => b.error)?.error,
)

const toggle = () => {
    toggled.value = !toggled.value
}

const onEnter = (el: Element) => {
    (el as HTMLElement).style.height = el.scrollHeight + 'px'
}

const onLeave = (el: Element) => {
    (el as HTMLElement).style.height = ''
}

watch(initialized, (val) => {
    if (val && INITIALLY_EXPANDED_BEHAVIORS.includes(effectiveRevealBehavior.value)) {
        toggled.value = true
    }
}, { immediate: true })

watch([childrenError, cpIsLoading], ([hasChildrenError, loading]) => {
    if (hasChildrenError && !loading && !NON_AUTO_REVEALING_BEHAVIORS.includes(effectiveRevealBehavior.value)) {
        toggled.value = true
    }
})

defineExpose({
    autoReveal: () => {
        if (!NON_AUTO_REVEALING_BEHAVIORS.includes(props.revealBehavior)) {
            toggled.value = true
        }
    },
})
</script>

<style lang="sass" scoped>
@import "./RevealBehaviorTile.sass"
</style>
